import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Upload, FileText, Loader2, Save, Trash2, Building2, Flame,
  Sparkles, CheckCircle, BookOpen, FileSearch,
} from "lucide-react";

const RAMOS = ["Incendio y Líneas Aliadas"];
const ASEGURADORAS_DEFAULT = [
  "Mapfre BHD Seguros", "La Colonial", "Seguros SURA",
  "Seguros Universal", "Humano Seguros", "Seguros Crecer",
];
const ADD_NEW = "__add_new__";

export const ModelosAseguradoras = () => {
  const { getAuthHeaders } = useAuth();

  const [ramo, setRamo] = useState(RAMOS[0]);
  const [aseguradoraSel, setAseguradoraSel] = useState("");
  const [aseguradoraCustom, setAseguradoraCustom] = useState("");
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null); // {terminologia, campos_detectados, filename, aseguradora_detectada}
  const [mapeos, setMapeos] = useState([]); // [{termino, campo_sistema, valor_ejemplo, confianza}]
  const [camposSistema, setCamposSistema] = useState([]);
  const [nombreModelo, setNombreModelo] = useState("");
  const [saving, setSaving] = useState(false);

  const [modelos, setModelos] = useState([]);
  const [aseguradorasUsadas, setAseguradorasUsadas] = useState([]);

  const aseguradoraFinal = aseguradoraSel === ADD_NEW ? aseguradoraCustom.trim() : aseguradoraSel;
  const aseguradoraOptions = Array.from(new Set([...ASEGURADORAS_DEFAULT, ...aseguradorasUsadas]));

  const cargarModelos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/aprendizaje/modelos`, getAuthHeaders());
      setModelos(res.data.modelos || []);
      setAseguradorasUsadas(res.data.aseguradoras_usadas || []);
    } catch (e) {
      console.error("Error cargando modelos", e);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    cargarModelos();
    axios.get(`${API}/aprendizaje/campos-sistema`, getAuthHeaders())
      .then((r) => setCamposSistema(Array.isArray(r.data) ? r.data : (r.data.campos || [])))
      .catch(() => {});
  }, [cargarModelos, getAuthHeaders]);

  const onDrop = useCallback((accepted) => {
    if (accepted && accepted[0]) {
      setFile(accepted[0]);
      setAnalysis(null);
      setMapeos([]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg", ".webp"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
    maxFiles: 1,
  });

  const analizar = async () => {
    if (!file) return toast.error("Selecciona un archivo del modelo");
    if (!aseguradoraFinal) return toast.error("Selecciona o escribe la aseguradora");
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("ramo", ramo);
      fd.append("aseguradora", aseguradoraFinal);
      const res = await axios.post(`${API}/aprendizaje/modelos/analizar`, fd, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" },
      });
      if (!res.data.success) {
        toast.error(res.data.error || "No se pudo analizar el documento");
        return;
      }
      setAnalysis(res.data);
      if (res.data.campos_sistema?.length) setCamposSistema(res.data.campos_sistema);
      const detectados = res.data.campos_detectados || [];
      setMapeos(detectados.map((c) => ({
        termino: c.termino || "",
        campo_sistema: c.campo_sugerido_id || "sin_mapear",
        valor_ejemplo: c.valor_ejemplo || "",
        confianza: c.confianza || "media",
        nuevo: !!c.nuevo,
      })));
      setNombreModelo(`${aseguradoraFinal} – ${ramo}`);
      const nuevos = (res.data.campos_nuevos || []).length;
      toast.success(
        `Documento analizado: ${detectados.length} campo(s) detectado(s)` +
        (nuevos ? ` · ${nuevos} variable(s) nueva(s) agregada(s) al sistema y auto-mapeada(s)` : "")
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error analizando el documento");
    } finally {
      setAnalyzing(false);
    }
  };

  const updateMapeo = (idx, campo_sistema) => {
    setMapeos((prev) => prev.map((m, i) => (i === idx ? { ...m, campo_sistema } : m)));
  };

  const guardar = async () => {
    if (!analysis) return;
    setSaving(true);
    try {
      const campos_mapeados = mapeos
        .filter((m) => m.termino)
        .map((m) => {
          const cs = camposSistema.find((c) => c.id === m.campo_sistema);
          return {
            termino: m.termino,
            campo_sistema: m.campo_sistema === "sin_mapear" ? "" : m.campo_sistema,
            campo_sistema_label: cs ? cs.label : "",
            valor_ejemplo: m.valor_ejemplo,
          };
        });
      const res = await axios.post(`${API}/aprendizaje/modelos`, {
        ramo,
        aseguradora: aseguradoraFinal,
        nombre_modelo: nombreModelo,
        filename: analysis.filename,
        terminologia: analysis.terminologia,
        campos_mapeados,
      }, getAuthHeaders());
      toast.success(`Modelo guardado. ${res.data.sinonimos_agregados || 0} término(s) agregados al diccionario.`);
      setFile(null);
      setAnalysis(null);
      setMapeos([]);
      setAseguradoraCustom("");
      cargarModelos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar el modelo");
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este modelo de referencia?")) return;
    try {
      await axios.delete(`${API}/aprendizaje/modelos/${id}`, getAuthHeaders());
      toast.success("Modelo eliminado");
      cargarModelos();
    } catch (e) {
      toast.error("No se pudo eliminar el modelo");
    }
  };

  const confColor = (c) => ({
    alta: "bg-green-100 text-green-700",
    media: "bg-amber-100 text-amber-700",
    baja: "bg-slate-100 text-slate-600",
  }[c] || "bg-slate-100 text-slate-600");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="modelos-aseguradoras-tab">
      {/* Columna izquierda: subir + analizar */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#193966]">
              <Flame className="w-5 h-5 text-orange-500" /> Cargar Modelo de Aseguradora
            </CardTitle>
            <CardDescription>
              Sube el modelo de factura/póliza de una aseguradora. La IA lo lee, aprende su
              terminología y detecta sus campos para que los mapees con los del sistema.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Ramo</Label>
                <Select value={ramo} onValueChange={setRamo}>
                  <SelectTrigger data-testid="modelo-ramo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RAMOS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">Aseguradora</Label>
                <Select value={aseguradoraSel} onValueChange={setAseguradoraSel}>
                  <SelectTrigger data-testid="modelo-aseguradora-select">
                    <SelectValue placeholder="Selecciona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {aseguradoraOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    <SelectItem value={ADD_NEW}>➕ Agregar nueva...</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {aseguradoraSel === ADD_NEW && (
              <Input
                placeholder="Nombre de la nueva aseguradora"
                value={aseguradoraCustom}
                onChange={(e) => setAseguradoraCustom(e.target.value)}
                data-testid="modelo-aseguradora-custom-input"
              />
            )}

            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragActive ? "border-[#193966] bg-blue-50" : "border-slate-300 hover:border-[#193966]"
              }`}
              data-testid="modelo-dropzone"
            >
              <input {...getInputProps()} />
              <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
              {file ? (
                <p className="text-sm font-medium text-[#193966]">{file.name}</p>
              ) : (
                <p className="text-sm text-slate-500">Arrastra el documento aquí o haz clic (PDF, imagen o Word)</p>
              )}
            </div>

            <Button
              onClick={analizar}
              disabled={analyzing || !file || !aseguradoraFinal}
              className="w-full bg-[#193966] hover:bg-[#142c4f] text-white"
              data-testid="modelo-analizar-btn"
            >
              {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {analyzing ? "Analizando con IA..." : "Analizar y aprender"}
            </Button>
          </CardContent>
        </Card>

        {analysis && (
          <Card data-testid="modelo-terminologia-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#193966] text-base">
                <BookOpen className="w-5 h-5" /> Terminología aprendida
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                {analysis.terminologia || "Sin resumen de terminología."}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Columna derecha: mapeo + guardar */}
      <div className="space-y-6">
        {analysis ? (
          <Card data-testid="modelo-mapeo-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#193966] text-base">
                <FileSearch className="w-5 h-5" /> Mapeo de campos detectados
              </CardTitle>
              <CardDescription>
                Relaciona cada término del documento con el campo equivalente del sistema.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="mb-1.5 block">Nombre del modelo</Label>
                <Input value={nombreModelo} onChange={(e) => setNombreModelo(e.target.value)} data-testid="modelo-nombre-input" />
              </div>
              <ScrollArea className="h-[360px] rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Término en el documento</TableHead>
                      <TableHead>Campo del sistema</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mapeos.length === 0 && (
                      <TableRow><TableCell colSpan={2} className="text-center text-slate-400 py-6">No se detectaron campos.</TableCell></TableRow>
                    )}
                    {mapeos.map((m, idx) => (
                      <TableRow key={idx} data-testid={`modelo-mapeo-row-${idx}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800">{m.termino}</span>
                            <Badge className={`${confColor(m.confianza)} border-0 text-[10px]`}>{m.confianza}</Badge>
                            {m.nuevo && (
                              <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px]" data-testid={`modelo-mapeo-nuevo-${idx}`}>
                                <Sparkles className="w-2.5 h-2.5 mr-1" /> Nuevo
                              </Badge>
                            )}
                          </div>
                          {m.valor_ejemplo && <p className="text-xs text-slate-400 mt-0.5">Ej: {m.valor_ejemplo}</p>}
                        </TableCell>
                        <TableCell>
                          <Select value={m.campo_sistema} onValueChange={(v) => updateMapeo(idx, v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`modelo-mapeo-select-${idx}`}>
                              <SelectValue placeholder="Sin mapear" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sin_mapear">— Sin mapear —</SelectItem>
                              {camposSistema.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
              <Button onClick={guardar} disabled={saving} className="w-full bg-[#80BC00] hover:bg-[#6da200] text-white" data-testid="modelo-guardar-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar modelo de referencia
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center text-slate-400">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
              Sube y analiza un modelo para mapear sus campos.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Lista de modelos guardados */}
      <div className="lg:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#193966] text-base">
              <Building2 className="w-5 h-5" /> Modelos guardados ({modelos.length})
            </CardTitle>
            <CardDescription>
              Estos modelos se consultan automáticamente al realizar una nueva comparación para
              reconocer la aseguradora y su terminología.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {modelos.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Aún no has guardado modelos.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {modelos.map((m) => (
                  <div key={m.id} className="border rounded-lg p-3 bg-white" data-testid={`modelo-card-${m.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-sm text-slate-800">{m.nombre_modelo || m.aseguradora}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]"><Building2 className="w-2.5 h-2.5 mr-1" />{m.aseguradora}</Badge>
                          <Badge className="bg-orange-100 text-orange-700 border-0 text-[10px]"><Flame className="w-2.5 h-2.5 mr-1" />{m.ramo}</Badge>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:bg-red-50" onClick={() => eliminar(m.id)} data-testid={`modelo-delete-${m.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-2 line-clamp-3">{m.terminologia || "Sin terminología."}</p>
                    <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      {(m.campos_mapeados || []).length} campo(s) mapeado(s)
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
